import { useRef, useState, type KeyboardEvent } from 'react';
import { Star } from 'lucide-react';
import { FormControl } from '@/components/ui/form';
import { cn } from '@/lib/utils';
import {
  SURVEY_RATING_DEFAULT_MAX,
  SURVEY_RATING_DEFAULT_MIN,
  SURVEY_RATING_SCALE,
} from '../../constants/survey.constants';

interface RatingAnswerInputProps {
  value: string;
  onChange: (value: string) => void;
  invalid: boolean;
}

export function RatingAnswerInput({ value, onChange, invalid }: RatingAnswerInputProps) {
  const [hovered, setHovered] = useState(0);
  const buttonsRef = useRef<(HTMLButtonElement | null)[]>([]);
  const selected = Number(value) || 0;
  const active = hovered || selected;

  function selectPosition(position: number) {
    const clamped = Math.min(
      SURVEY_RATING_DEFAULT_MAX,
      Math.max(SURVEY_RATING_DEFAULT_MIN, position),
    );
    onChange(String(clamped));
    buttonsRef.current[clamped - SURVEY_RATING_DEFAULT_MIN]?.focus();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const current = selected || SURVEY_RATING_DEFAULT_MIN;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        event.preventDefault();
        selectPosition(current + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        event.preventDefault();
        selectPosition(current - 1);
        break;
      case 'Home':
        event.preventDefault();
        selectPosition(SURVEY_RATING_DEFAULT_MIN);
        break;
      case 'End':
        event.preventDefault();
        selectPosition(SURVEY_RATING_DEFAULT_MAX);
        break;
    }
  }

  return (
    <FormControl>
      <div
        role="radiogroup"
        aria-label="Rating"
        aria-invalid={invalid || undefined}
        className="flex flex-wrap items-center gap-1"
        onMouseLeave={() => setHovered(0)}
        onKeyDown={handleKeyDown}
      >
        {SURVEY_RATING_SCALE.map((position, index) => {
          const isSelected = selected === position;
          return (
            <button
              key={position}
              ref={(node) => {
                buttonsRef.current[index] = node;
              }}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={`${position} out of ${SURVEY_RATING_SCALE.length}`}
              tabIndex={
                selected ? (isSelected ? 0 : -1) : position === SURVEY_RATING_DEFAULT_MIN ? 0 : -1
              }
              onClick={() => onChange(String(position))}
              onMouseEnter={() => setHovered(position)}
              className="rounded-md p-1 text-muted-foreground transition-colors hover:text-palette-amber focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <Star
                className={cn(
                  'size-7',
                  position <= active ? 'fill-palette-amber text-palette-amber' : 'fill-none',
                )}
                aria-hidden
              />
            </button>
          );
        })}
      </div>
    </FormControl>
  );
}
